import React, { useEffect, useState } from 'react'
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { PencilSquareIcon, XMarkIcon } from '@heroicons/react/16/solid'
import { Separator } from '@/components/ui/separator'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import Field from '@/components/ui/Field'
import FieldStatusMessage from '@/components/ui/FieldStatusMessage'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select'
import { useController, useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import useGetTeamDetails from '@/services/api/team/useGetTeamDetails'
import useUpdateTeam from '@/services/api/team/useUpdateTeam'
import {
  FORECAST_HORIZON_MONTHS,
  FORECAST_HORIZON_WEEKS,
} from '@/data/constants/constants'

const schema = z
  .object({
    name: z
      .string()
      .min(1, 'Space name is required.')
      .max(64, 'Space name cannot exceed 64 characters.'),
    period_type: z.string().min(1, 'Period type is required.'),
    default_horizon: z.number(),
    week_starts_on: z.number().min(0).max(6),
  })
  .refine(
    (data) => {
      if (data.period_type === 'Week')
        return FORECAST_HORIZON_WEEKS.includes(data.default_horizon)
      if (data.period_type === 'Month')
        return FORECAST_HORIZON_MONTHS.includes(data.default_horizon)
      return false
    },
    {
      message: 'Invalid forecast horizon for the selected period type.',
      path: ['default_horizon'],
    },
  )

type FormData = z.infer<typeof schema>

const WEEK_START_OPTIONS = [
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
  { value: 0, label: 'Sunday' },
]

export default function EditTeam({ teamId }: { teamId: number }) {
  const [open, onOpenChange] = useState(false)

  const { data: teamDetails } = useGetTeamDetails({ teamId: teamId.toString() })

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    reset,
    control,
    watch,
  } = useForm<FormData>({ resolver: zodResolver(schema) })

  const { field: periodTypeField } = useController({ control, name: 'period_type' })
  const { field: forecastHorizonField } = useController({ control, name: 'default_horizon' })
  const { field: weekStartsOnField } = useController({ control, name: 'week_starts_on' })

  const periodType = watch('period_type')

  const forecastOptions =
    periodType === 'Week'
      ? FORECAST_HORIZON_WEEKS.map((w) => ({ value: w, label: `${w} weeks` }))
      : periodType === 'Month'
        ? FORECAST_HORIZON_MONTHS.map((m) => ({ value: m, label: `${m} months` }))
        : []

  const {
    action: updateTeam,
    status: { isPending: isUpdating },
  } = useUpdateTeam({ setError })

  // Populate form each time the dialog opens (or when details first arrive)
  useEffect(() => {
    if (open && teamDetails?.space) {
      reset({
        name: teamDetails.space.name,
        period_type: teamDetails.space.period_type,
        default_horizon: teamDetails.space.default_horizon,
        week_starts_on: teamDetails.space.week_starts_on,
      })
    }
  }, [open, teamDetails, reset])

  async function onSubmit(data: FormData) {
    try {
      await updateTeam({ teamId, ...data })
      onOpenChange(false)
    } catch {
      // Server-side errors (e.g. name uniqueness) are set on the form by useUpdateTeam
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button
          className="h-max w-max p-[2px]"
          iconOnly
          variant="ghost"
          title="Edit space"
        >
          <PencilSquareIcon />
        </Button>
      </DialogTrigger>

      <DialogContent
        as="form"
        onSubmit={handleSubmit(onSubmit)}
        className="max-h-[37.5rem] min-h-[15rem]"
      >
        <DialogHeader>
          <DialogTitle>Edit Space</DialogTitle>
          <DialogClose>
            <XMarkIcon />
          </DialogClose>
        </DialogHeader>

        <Separator />

        <DialogBody className="flex flex-col gap-4">
          {/* Space Name */}
          <Field
            statusText={errors.name?.message}
            status={errors.name ? 'error' : null}
          >
            <Label>Space Name</Label>
            <Input
              {...register('name')}
              placeholder="Enter space name"
              autoFocus
            />
            <FieldStatusMessage />
          </Field>

          {/* Period Type + Forecast Horizon */}
          <div className="flex flex-wrap gap-4">
            <Field
              statusText={errors.period_type?.message}
              status={errors.period_type ? 'error' : null}
              className="min-w-[140px] flex-1"
            >
              <Label>Period Type</Label>
              <Select
                value={periodTypeField.value}
                onValueChange={(val) => {
                  periodTypeField.onChange(val)
                  // Reset horizon when period changes to avoid an invalid combination
                  forecastHorizonField.onChange(
                    val === 'Week' ? FORECAST_HORIZON_WEEKS[0] : FORECAST_HORIZON_MONTHS[0],
                  )
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select period type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Month">Month</SelectItem>
                  <SelectItem value="Week">Week</SelectItem>
                </SelectContent>
              </Select>
              <FieldStatusMessage />
            </Field>

            <Field
              key={periodType}
              statusText={errors.default_horizon?.message}
              status={errors.default_horizon ? 'error' : null}
              className="min-w-[160px] flex-1"
            >
              <Label>Default Forecast Horizon</Label>
              <Select
                value={forecastHorizonField.value?.toString()}
                onValueChange={(val) => forecastHorizonField.onChange(Number(val))}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select forecast horizon" />
                </SelectTrigger>
                <SelectContent>
                  {forecastOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value.toString()}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FieldStatusMessage />
            </Field>
          </div>

          {/* Week Starts On */}
          <Field
            statusText={errors.week_starts_on?.message}
            status={errors.week_starts_on ? 'error' : null}
          >
            <Label>Week Starts On</Label>
            <Select
              value={weekStartsOnField.value?.toString()}
              onValueChange={(val) => weekStartsOnField.onChange(Number(val))}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select week start day" />
              </SelectTrigger>
              <SelectContent>
                {WEEK_START_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value.toString()}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FieldStatusMessage />
          </Field>
        </DialogBody>

        <Separator />

        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="secondary">
              Cancel
            </Button>
          </DialogClose>
          <Button type="submit" loading={isUpdating} disabled={isUpdating}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
